/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, FormLabel, Grid } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { InstantSearch, Configure } from 'react-instantsearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { InstantSearchFieldAdaptor } from '@/components/SearchField';
import { RefinementListSelect } from '@/components/selects/RefinementListSelect';
import { TableRefresh } from '@/components/table/TableRefresh';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import RefreshPolicyButton from '@/components/buttons/RefreshPolicyButton';
import { DealStageListSelect } from '@/components/table/DealStageListSelect';
import { usePipelineContext } from '@/context/pipelineContext';
import InstantSearchDataGrid from '@/components/tables/InstantSearchDataGrid';
import { parseHeadCells } from '@/components/tables/InstantSearchDataGrid/data';
import { AddNewDealButton } from '@/components/buttons/AddNewDealButton';
import { useGetDealsByClientIdQuery } from '@/features/deals/dealsApi';
import { getClientIdFilters } from '@/features/clients/helper';

// Storage key for filters
const FILTERS_STORAGE_KEY = 'policyTransactionFilters';
const DEAL_FILTER_STORAGE_KEY = 'policyTransactionDealFilter';
const ROWS_PER_PAGE_STORAGE_KEY = 'policyTransactionRowsPerPage';

const getStageFilter = (stages = []) =>
	`(dealStage:=[${stages.map((id) => `"${id}"`).join(', ')}] && status:!="Archived")`;

export const PolicyTransactionTab = () => {
	const { clientRow } = useObjectContext();

	const { currentData: clientDeals = [] } = useGetDealsByClientIdQuery(clientRow?.clientId, {
		skip: !clientRow?.clientId,
	});

	const clientDealIds = useMemo(() => {
		return clientDeals
			.filter(
				(deal) =>
					deal.pipeline !== process.env.REACT_APP_PIPELINE_CLAIMS &&
					deal.pipeline !== process.env.REACT_APP_PIPELINE_DEBTORS,
			)
			.map((deal) => deal.dealId);
	}, [clientDeals]);

	const [showDialog, setShowDialog] = useState(false);

	// Load saved filters from localStorage
	const loadSavedFilters = () => {
		try {
			const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
			const savedDealFilter = localStorage.getItem(DEAL_FILTER_STORAGE_KEY);
			const savedRowsPerPage = localStorage.getItem(ROWS_PER_PAGE_STORAGE_KEY);
			const { version, ...parsedFilters } = savedFilters ? JSON.parse(savedFilters) : {};
			const isCurrentVersion = version === process.env.REACT_APP_FILTERS_VERSION;

			return {
				filters: isCurrentVersion ? parsedFilters : null,
				dealFilter: (isCurrentVersion && savedDealFilter) || 'all',
				rowsPerPage: savedRowsPerPage ? parseInt(savedRowsPerPage, 10) : 20,
			};
		} catch (error) {
			console.error('Error loading saved filters:', error);
			return { filters: null, dealFilter: 'all', rowsPerPage: 20 };
		}
	};

	const savedState = loadSavedFilters();

	const [filters, setFilters] = useState({
		dealStage: [],
		status: [],
		isActiveStage: false,
		isInTermStage: false,
		isClosedStage: false,
		...(savedState.filters || {}),
	});

	const [dealFilter, setDealFilter] = useState(savedState.dealFilter);
	const [rowsPerPage, setRowsPerPage] = useState(savedState.rowsPerPage);

	// Save filters to localStorage whenever they change
	useEffect(() => {
		try {
			localStorage.setItem(
				FILTERS_STORAGE_KEY,
				JSON.stringify({ ...filters, version: process.env.REACT_APP_FILTERS_VERSION }),
			);
		} catch (error) {
			console.error('Error saving filters:', error);
		}
	}, [filters]);

	// Save dealFilter to localStorage
	useEffect(() => {
		try {
			localStorage.setItem(DEAL_FILTER_STORAGE_KEY, dealFilter);
		} catch (error) {
			console.error('Error saving dealFilter:', error);
		}
	}, [dealFilter]);

	// Save rowsPerPage to localStorage
	useEffect(() => {
		try {
			localStorage.setItem(ROWS_PER_PAGE_STORAGE_KEY, rowsPerPage.toString());
		} catch (error) {
			console.error('Error saving rowsPerPage:', error);
		}
	}, [rowsPerPage]);

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
	};

	const { active, inactive, closed } = usePipelineContext();
	const [activeIds, inactiveIds, closedIds] = useMemo(
		() => [
			active.map((c) => c.id.toString()),
			inactive.map((c) => c.id.toString()),
			closed.map((c) => c.id.toString()),
		],
		[active, inactive, closed],
	);

	const excludedPipelines = [
		process.env.REACT_APP_PIPELINE_CLAIMS,
		process.env.REACT_APP_PIPELINE_DEBTORS,
	];

	const clientFilter = getClientIdFilters(clientRow);

	const filterSchema = useMemo(() => {
		const pipelineFilter = excludedPipelines
			.filter(Boolean)
			.map((pipeline) => `pipeline:!=${pipeline}`)
			.join(' && ');

		const inTermStageIds = inactiveIds;
		const activeStageIds = activeIds;

		const selectedInTermStages = filters.dealStage.filter((id) => inTermStageIds.includes(id));
		const selectedActiveStages = filters.dealStage.filter((id) => activeStageIds.includes(id));
		const selectedOtherStages = filters.dealStage.filter(
			(id) => !inTermStageIds.includes(id) && !activeStageIds.includes(id),
		);

		let stageFilter = '';
		if (filters.dealStage.length > 0) {
			const inTermFilter =
				selectedInTermStages.length > 0 ? getStageFilter(selectedInTermStages) : '';
			const activeFilter =
				selectedActiveStages.length > 0 ? getStageFilter(selectedActiveStages) : '';
			const otherStagesFilter =
				selectedOtherStages.length > 0 ? getStageFilter(selectedOtherStages) : '';

			const stageFilters = [inTermFilter, activeFilter, otherStagesFilter].filter(Boolean);

			if (stageFilters.length > 1) {
				stageFilter = `(${stageFilters.join(' || ')})`;
			} else {
				stageFilter = stageFilters[0] ?? '';
			}
		}

		const archivedSelected = filters.status.includes('Archived');
		const statusFilter = archivedSelected ? 'status:="Archived"' : '';

		const combinedFilters = [stageFilter, statusFilter].filter(Boolean).join(' || ');

		let finalCombinedFilter = '';

		if (combinedFilters) {
			finalCombinedFilter = `(${combinedFilters})`;
		}

		const joinedFilters = [
			clientFilter,
			pipelineFilter,
			dealFilter === 'active' ? 'hasActivePolicyTransaction:=true' : '',
			dealFilter !== 'all' && dealFilter !== 'active' ? `yearRoot:="${dealFilter}"` : '',
			finalCombinedFilter,
		]
			.filter(Boolean)
			.join(' && ');
		console.log('Filter schema', joinedFilters);
		return {
			filters: joinedFilters,
		};
	}, [clientFilter, dealFilter, filters, inactiveIds, activeIds, closedIds]);

	useEffect(() => {
		if (clientDealIds.length > 0 && clientRow?.clientId && !savedState.filters) {
			// Only apply default filters if there are no saved filters
			setFilters({
				dealStage: [...activeIds, ...inactiveIds],
				status: [],
				isActiveStage: true,
				isInTermStage: true,
				isClosedStage: false,
			});
		}
	}, [clientDealIds, clientRow?.clientId, activeIds, inactiveIds]);

	const headCells = [
		{ id: 'yearFolder', numeric: false, label: 'Year', align: 'left' },
		{ id: 'dealName', numeric: false, label: 'Deal name', align: 'left' },
		{ id: 'policies', numeric: false, label: 'Policies', align: 'left', sortable: false },
		{ id: 'pipeline', numeric: false, label: 'Type', align: 'left' },
		{ id: 'renewalDate', numeric: false, date: true, label: 'Renewal date', align: 'left' },
		{ id: 'dealStatus', numeric: false, label: 'Status/Stage', align: 'left', sortable: false },
		{
			id: 'actions',
			numeric: false,
			label: '',
			align: 'left',
			hideable: false,
			reorderable: false,
		},
	];
	const columns = useMemo(() => parseHeadCells(headCells), [headCells]);

	return (
		<>
			<InstantSearch indexName='deals' searchClient={dealSearch}>
				<Configure {...filterSchema} />
				<TableRefresh />

				<Stack
					direction='row'
					sx={{
						pb: 2,
						width: '100%',
						alignItems: 'end',
					}}
				>
					<Grid container spacing={1} alignItems='flex-end' sx={{ width: '100%' }}>
						<Grid size={{ xs: 12, md: 6 }}>
							<FormLabel>Search</FormLabel>
							<InstantSearchFieldAdaptor />
						</Grid>

						<Grid size={{ xs: 12, md: 3 }}>
							<DealStageListSelect
								width='100%'
								multiple={true}
								filters={filters}
								onFilterChange={setFilters}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 3 }}>
							<RefinementListSelect
								attribute='yearRoot'
								multiple={true}
								title={'Year'}
							/>
						</Grid>
					</Grid>
					<Box sx={{ mb: 0.5, ml: 1 }}>
						<RefreshPolicyButton />
					</Box>
					<Box sx={{ mb: 0.5, ml: 1 }}>
						<AddNewDealButton pipeline={process.env.REACT_APP_PIPELINE_NEW_BUSINESS} />
					</Box>
				</Stack>

				<InstantSearchDataGrid
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					objectType={'deal'}
					columns={columns}
					includePolicies={true}
					includeTransactions={true}
					variant={'clientDeals'}
					showPreview={false}
					enableExport={false}
					enableColumnReorder
					slotProps={{
						box: {
							minHeight: '6em',
							maxHeight: '100%',
						},
					}}
				/>
			</InstantSearch>
			{showDialog && (
				<ConfirmDialog
					openDialog={showDialog}
					handleClose={() => setShowDialog(false)}
					handleConfirm={() => {}}
					actionName={'Convert to client'}
					canBeUndone={true}
					additionalContent={
						'Adding a new business will convert this prospect to a client.'
					}
				/>
			)}
		</>
	);
};
PolicyTransactionTab.displayName = 'PolicyTransactionTab';
